import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import { writeFieldToForm } from "./formFieldHelpers";
import TextField from "../../../common/formFields/TextField";

const CASE_NUMBER_MAX_LENGTH = 40;

export interface CaseNumberFieldProps {
    /** Currently entered Case number (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: string;
    /**
     * Whether the field is required - varies by mount site in the classic
     * pane (Details tab: required for [3, 53].includes(requestType); DDS/
     * Requestor tab: always required, since it only mounts there for
     * Disability/Social Security). Computed by the caller, not this
     * component, since the two mount sites have different rules.
     */
    readonly required: boolean;
    readonly dispatch: Function;
    readonly formName?: string;
    /**
     * Extra, non-form side effect the caller wants to run on blur (the
     * classic pane's autosave PATCH - fires onBlur there, not onChange, to
     * avoid a PATCH per keystroke). The base DATA_ENTRY_FIELDS_FORM write
     * happens on every change via this component's own writeFieldToForm.
     */
    readonly onCaseNumberBlur?: (value?: string) => void;
}

/**
 * Shared Case Number field (Spec #34098 / F1a). The same redux-form field
 * (FormConstants.CASE_NUMBER) is mounted at two different places in the
 * classic RightPane depending on request type - this component doesn't
 * know or care which; the caller decides whether/where to render it and
 * what `required` should be.
 */
const CaseNumberField: React.FC<CaseNumberFieldProps> = (props) => {
    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;

    const onChange = (value: string) => {
        writeFieldToForm(props.dispatch, FormConstants.CASE_NUMBER, value, formName);
    };

    const onBlur = () => {
        props.onCaseNumberBlur?.(props.value);
    };

    return (
        <TextField
            label="Case number"
            placeholder="123456789"
            maxLength={CASE_NUMBER_MAX_LENGTH}
            required={props.required}
            onChange={onChange}
            onBlur={onBlur}
            value={props.value}
            suppressEnterSubmit
        />
    );
};

export default CaseNumberField;

export const CASE_NUMBER_ROW_ID = "caseNumber";

/** Builds the FieldGroupRow for the Case Number row. */
export function mapCaseNumberToFieldGroupRow(badgeNumber: number, required: boolean, formValue: string | undefined): FieldGroupRow {
    const hasValue = Boolean(formValue);

    return {
        id: CASE_NUMBER_ROW_ID,
        badgeNumber,
        status: hasValue
            ? FieldGroupRowStatus.Confirmed
            : (required ? FieldGroupRowStatus.NeedsEntry : FieldGroupRowStatus.EmptyNotRequired),
        label: "Case Number",
        value: hasValue ? String(formValue) : null,
        isAiPreSelected: false,
    };
}
